/** @typedef {import('@/types/api').PosReceipt} PosReceipt */

export const THERMAL_RECEIPT_WIDTH_CHARS = 42

/**
 * @param {string | number | null | undefined} amount
 * @param {string} [currency]
 */
export function formatReceiptMoney(amount, currency = 'AED') {
  const value = Number(amount)
  const formatted = Number.isFinite(value) ? value.toFixed(2) : '0.00'
  return `${currency} ${formatted}`
}

/**
 * @param {string | null | undefined} iso
 */
export function formatReceiptDateTime(iso) {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * @param {PosReceipt | null | undefined} receipt
 */
export function getReceiptGuestLabel(receipt) {
  return receipt?.customer?.name ?? 'Walk-in'
}

/**
 * @param {string} left
 * @param {string} right
 * @param {number} [width]
 */
export function alignReceiptColumns(left, right, width = THERMAL_RECEIPT_WIDTH_CHARS) {
  const trimmedLeft = left.trim()
  const trimmedRight = right.trim()
  const gap = Math.max(1, width - trimmedLeft.length - trimmedRight.length)
  return `${trimmedLeft}${' '.repeat(gap)}${trimmedRight}`
}

/**
 * @param {import('@/types/api').PosReceiptLine | { description?: string, name_ar?: string | null }} line
 * @returns {{ text: string, dir: 'ltr' | 'rtl' }[]}
 */
export function receiptLineDescriptions(line) {
  const primary = line.description ?? ''
  const ar = line.name_ar?.trim()
  if (!ar) {
    return [{ text: primary, dir: 'ltr' }]
  }

  return [
    { text: primary, dir: 'ltr' },
    { text: ar, dir: 'rtl' },
  ]
}

/**
 * @param {import('@/types/api').PosReceiptLine | { description?: string, name_ar?: string | null }} line
 */
export function formatReceiptLineDescription(line) {
  const parts = receiptLineDescriptions(line)
  if (parts.length === 1) {
    return parts[0].text
  }

  return `${parts[0].text} / ${parts[1].text}`
}

/**
 * @param {PosReceipt | null | undefined} receipt
 */
export function buildReceiptPreviewLines(receipt) {
  if (!receipt) return []

  const currency = receipt.sale?.currency_code ?? 'AED'
  const lines = [
    receipt.branch?.name ?? '',
    receipt.branch?.address ?? '',
    receipt.branch?.trn ? `TRN: ${receipt.branch.trn}` : '',
    '------------------------------------------',
    alignReceiptColumns('Receipt', receipt.sale?.sale_number ?? ''),
    alignReceiptColumns('Date', formatReceiptDateTime(receipt.sale?.completed_at)),
    alignReceiptColumns('Guest', getReceiptGuestLabel(receipt)),
    '------------------------------------------',
  ]

  for (const line of receipt.lines ?? []) {
    const prefix = line.is_addon ? '+ ' : ''
    const qty = line.quantity > 1 ? ` x${line.quantity}` : ''
    const descriptions = receiptLineDescriptions(line)
    const primaryLabel = `${prefix}${descriptions[0].text}${qty}`
    lines.push(alignReceiptColumns(
      primaryLabel,
      formatReceiptMoney(line.line_total, currency).replace(`${currency} `, ''),
    ))
    if (descriptions[1]) {
      lines.push(`  ${descriptions[1].text}`)
    }
    if (line.stylist_name) {
      lines.push(`  Stylist: ${line.stylist_name}`)
    }
  }

  lines.push('------------------------------------------')
  lines.push(alignReceiptColumns('Subtotal', receipt.totals?.subtotal ?? '0.00'))
  if (Number(receipt.totals?.discount) > 0) {
    lines.push(alignReceiptColumns('Discount', `-${receipt.totals.discount}`))
  }
  lines.push(alignReceiptColumns(receipt.tax_label ?? 'VAT', receipt.totals?.tax ?? '0.00'))
  if (Number(receipt.totals?.tip) > 0) {
    lines.push(alignReceiptColumns('Tip', receipt.totals.tip))
  }
  lines.push(alignReceiptColumns('TOTAL', receipt.totals?.total ?? '0.00'))
  lines.push('------------------------------------------')

  for (const payment of receipt.payments ?? []) {
    const label = payment.reference
      ? `${payment.method_name} (${payment.reference})`
      : payment.method_name ?? 'Payment'
    lines.push(alignReceiptColumns(label, payment.amount ?? '0.00'))
  }

  if (receipt.footer) {
    lines.push('', receipt.footer)
  }

  return lines.filter((line, index, all) => line !== '' || all[index + 1] !== '')
}

/**
 * @param {HTMLElement | null | undefined} node
 */
export function printThermalReceipt(node) {
  if (!node) return

  document.body.classList.add('printing-thermal')
  window.print()
  window.addEventListener('afterprint', () => {
    document.body.classList.remove('printing-thermal')
  }, { once: true })
}
